import {
	IconCircleInfoOutline18 as Info,
	IconLockOutline18 as Lock,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import { Badge, Button, IconButton, Tooltip } from "@productlane/ui"

import type { Mode } from "../model/rule-modes"
import type { FieldRowProps } from "./rule-editor-props"
import { toast } from "@/components/toast"
import { FieldNotice } from "../inputs/field-notice"
import { OptionSelect } from "../inputs/option-select"
import { PathText } from "../inputs/policy-token-highlight"
import {
	carryChain,
	defaultRuleFor,
	MODE_HINTS,
	MODE_LABELS,
	modesFor,
	unsatisfiableReason,
} from "../model/rule-modes"
import { formatToolDescription } from "../model/tool-description"
import { needsJsonCast } from "../model/typed-value"
import { RuleEditor } from "./rule-editor"

/** One argument of the tool: what it is, what the agent may write, and why not. */
export function FieldRow({
	field,
	rule,
	onChange,
	onRemove,
	shadowedBy,
	...editor
}: FieldRowProps) {
	const indent = Math.min(field.depth, 3) * 14
	const unsatisfiable = shadowedBy ? null : unsatisfiableReason(field, rule)
	const castNeeded = !shadowedBy && needsJsonCast(field, rule)

	return (
		<div
			className="rounded-md px-1 py-1.5"
			style={{ paddingLeft: indent + 4 }}
			data-testid={`policy-field-${field.path}`}
		>
			<div className="flex items-center justify-between gap-3">
				<span className="flex min-w-0 items-center gap-1.5">
					<PathText path={field.name} className="truncate font-mono text-xs" />
					<Badge variant="transparent">{field.type}</Badge>
					{field.required ? <Badge variant="dark-gray">required</Badge> : null}
					{field.description ? (
						<Tooltip
							label={
								<span className="block max-h-48 max-w-64 overflow-y-auto whitespace-pre-line">
									{formatToolDescription(field.description)}
								</span>
							}
							shouldWrapChildren
						>
							<Info className="h-3.5 w-3.5 flex-none text-tertiary" />
						</Tooltip>
					) : null}
				</span>

				<span className="flex flex-none items-center gap-1">
					{shadowedBy ? (
						<Tooltip
							label={`Covered by the rule on ${shadowedBy}`}
							shouldWrapChildren
						>
							<Badge variant="dark-gray">covered</Badge>
						</Tooltip>
					) : null}
					<OptionSelect
						align="end"
						triggerClassName="min-w-36"
						disabled={!!shadowedBy}
						value={rule.mode}
						options={modesFor(field).map((mode) => ({
							value: mode,
							label: MODE_LABELS[mode],
							hint: MODE_HINTS[mode],
							// The server rejects the call without a required argument, so
							// dropping one would break the tool rather than guard it.
							unavailable: mode === "blocked" && field.required,
						}))}
						onSelect={(mode: Mode) =>
							onChange(carryChain(rule, defaultRuleFor(mode, field)))
						}
						onUnavailable={() =>
							toast.info({
								title: `"${field.name}" is required by this tool`,
								description:
									"Set a fixed value, a reference, or a list of allowed values instead.",
								icon: Lock,
							})
						}
					/>
					{onRemove ? (
						<IconButton Icon={XIcon} variant="ghost" onClick={onRemove} />
					) : null}
				</span>
			</div>

			{shadowedBy ? null : (
				<RuleEditor {...editor} field={field} rule={rule} onChange={onChange} />
			)}

			{unsatisfiable ? (
				<FieldNotice
					tone="error"
					action={
						<Button
							variant="outline"
							size="xs"
							onClick={() => onChange(defaultRuleFor("fixed", field))}
						>
							Switch to Fixed value
						</Button>
					}
				>
					{unsatisfiable}
				</FieldNotice>
			) : null}

			{castNeeded ? (
				<FieldNotice
					action={
						<Button
							variant="outline"
							size="xs"
							onClick={() =>
								onChange(
									rule.mode === "ref" || rule.mode === "template"
										? { ...rule, cast: "json" }
										: rule,
								)
							}
						>
							Read as JSON
						</Button>
					}
				>
					This argument takes {field.kinds.join(" or ")}, so the text has to be
					read as JSON before it is sent.
				</FieldNotice>
			) : null}
		</div>
	)
}
